<template>
  <div class="au-formgroup">
    <!-- Request Form -->
    <template v-if="!submitted">
      <form
        class="au-card"
        :aria-label="STRINGS.aria.labels.forgotPasswordForm"
        @submit.prevent="handleSubmit"
        autocomplete="on"
        novalidate
      >
        <p class="au-flavor au-flavor--sub">{{ STRINGS.auth.passwordReset.subheading }}</p>

        <!-- Email -->
        <div class="au-field">
          <label for="forgot-email" class="au-label">{{ STRINGS.auth.labels.email }}</label>
          <div class="au-input-wrap">
            <InputText
              id="forgot-email"
              ref="emailInput"
              v-model="email"
              type="email"
              name="email"
              class="au-input"
              :placeholder="STRINGS.auth.placeholders.email"
              autocomplete="username"
              :aria-invalid="!!displayError"
              data-testid="forgot-email-input"
              @keyup.enter="handleSubmit"
            />
          </div>
        </div>

        <!-- Error display -->
        <p
          v-if="displayError"
          ref="errorRef"
          class="au-hint au-hint--err text-center"
          role="alert"
          aria-live="polite"
          tabindex="-1"
        >
          {{ displayError }}
        </p>

        <!-- Submit button -->
        <button
          type="button"
          class="au-btn au-btn--primary au-mt"
          :disabled="!canSubmit"
          data-testid="forgot-submit-button"
          @click="handleSubmit"
        >
          <template v-if="passwordReset.isLoading.value">
            {{ STRINGS.auth.buttons.sendingResetLink }}<LoadingDots />
          </template>
          <template v-else>
            {{ STRINGS.auth.buttons.sendResetLink }}
          </template>
        </button>
      </form>

      <!-- Footer links -->
      <div class="au-flinks">
        <button
          class="au-link au-link--muted"
          @click="emit('navigate', 'login')"
          data-testid="forgot-back-to-login-link"
        >
          {{ STRINGS.auth.links.backToLogin }}
        </button>
      </div>
    </template>

    <!-- Success State -->
    <template v-else>
      <div class="au-card au-card--center au-success">
        <span class="au-env">
          <svg
            width="46"
            height="46"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="5" width="18" height="14" rx="2.5" />
            <path d="M3.5 7.5l8.5 5.5 8.5-5.5" />
          </svg>
        </span>

        <h3 class="au-success-h">{{ STRINGS.auth.passwordReset.successHeading }}</h3>

        <p class="au-success-email" data-testid="forgot-success-email">{{ submittedEmail }}</p>

        <p class="au-gate-body">{{ STRINGS.auth.passwordReset.successExpiry }}</p>
      </div>

      <div class="au-flinks">
        <button
          class="au-link au-link--muted"
          @click="emit('navigate', 'login')"
          data-testid="forgot-success-back-to-login"
        >
          {{ STRINGS.auth.links.backToLogin }}
        </button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue';
import InputText from 'primevue/inputtext';
import LoadingDots from './ui/LoadingDots.vue';
import { usePasswordReset } from '@/composables/usePasswordReset';
import { isAuthError } from '@/types/auth';
import STRINGS from '@/constants/strings';
import { reportClientError, ErrorLocation } from '@/telemetry/errors';

const emit = defineEmits<{
  navigate: [target: 'login' | 'homepage'];
}>();

const passwordReset = usePasswordReset();

const email = ref('');
const submitted = ref(false);
const submittedEmail = ref('');
const emailInput = ref();
const errorRef = ref<HTMLElement>();

const displayError = computed(() => {
  if (passwordReset.rateLimitCountdown.value > 0) {
    return STRINGS.templates.rateLimitCountdown(passwordReset.rateLimitCountdown.value);
  }
  if (passwordReset.error.value) {
    return passwordReset.error.value.message;
  }
  return '';
});

const canSubmit = computed(() => {
  return (
    email.value.trim().length > 0 &&
    !passwordReset.isLoading.value &&
    passwordReset.rateLimitCountdown.value === 0
  );
});

async function handleSubmit(): Promise<void> {
  if (!canSubmit.value) return;

  passwordReset.clearError();

  try {
    await passwordReset.requestReset(email.value.trim());
    submittedEmail.value = email.value.trim();
    submitted.value = true;
  } catch (err: unknown) {
    if (!isAuthError(err)) {
      reportClientError(err, ErrorLocation.AuthForgotPasswordUnexpected);
    }
    focusError();
  }
}

function focusError(): void {
  nextTick(() => {
    errorRef.value?.focus();
  });
}

onMounted(() => {
  nextTick(() => {
    const el = emailInput.value?.$el ?? emailInput.value;
    const input = el?.querySelector?.('input') ?? el;
    if (input && typeof input.focus === 'function') {
      input.focus();
    }
  });
});
</script>
